import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { LoginPage } from '../features/auth/LoginPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { OAuthAuthorizePage } from '../features/auth/OAuthAuthorizePage';
import { PublicDocumentPage } from '../features/legal/PublicDocumentPage';
import { useAuth } from '../features/auth/AuthProvider';
import { AppShell } from './AppShell';

const AgentPage = lazy(async () => ({ default: (await import('../features/agent/AgentPage')).AgentPage }));
const AccessPage = lazy(async () => ({ default: (await import('../features/admin/AccessPage')).AccessPage }));
const UsersPage = lazy(async () => ({ default: (await import('../features/admin/UsersPage')).UsersPage }));
const CompaniesPage = lazy(async () => ({ default: (await import('../features/companies/CompaniesPage')).CompaniesPage }));
const CompanyDetailPage = lazy(async () => ({ default: (await import('../features/companies/CompanyDetailPage')).CompanyDetailPage }));
const KanbanPage = lazy(async () => ({ default: (await import('../features/kanban/KanbanPage')).KanbanPage }));
const TasksPage = lazy(async () => ({ default: (await import('../features/tasks/TasksPage')).TasksPage }));
const LeadershipPage = lazy(async () => ({ default: (await import('../features/reports/LeadershipPage')).LeadershipPage }));
const AuditPage = lazy(async () => ({ default: (await import('../features/admin/AuditPage')).AuditPage }));
const IntegrationsPage = lazy(async () => ({ default: (await import('../features/integrations/IntegrationsPage')).IntegrationsPage }));

function ProtectedLayout(): ReactNode {
  const auth = useAuth();
  const location = useLocation();
  if (auth.loading) return <main className="loading-page">Загрузка…</main>;
  if (!auth.session) {
    return <Navigate to="/login" replace state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }} />;
  }
  return <AppShell />;
}

function AdminLayout(): ReactNode {
  const auth = useAuth();
  const location = useLocation();
  if (auth.session?.actor.role !== 'admin') return <Navigate to={`/pipeline${location.search}`} replace />;
  return <Outlet />;
}

/** `/deals/:id` is a shareable link; the record itself opens over the funnel. */
function DealRedirect(): ReactNode {
  const location = useLocation();
  const id = useParams().id ?? '';
  const search = new URLSearchParams(location.search);
  search.set('deal', id);
  return <Navigate to={{ pathname: '/pipeline', search: `?${search.toString()}` }} replace />;
}

export function AppRouter(): ReactNode {
  return (
    <Suspense fallback={<main className="loading-page">Загрузка…</main>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
        <Route path="/privacy" element={<PublicDocumentPage kind="privacy" />} />
        <Route path="/terms" element={<PublicDocumentPage kind="terms" />} />
        <Route path="/support" element={<PublicDocumentPage kind="support" />} />
        <Route element={<ProtectedLayout />}>
          <Route path="/pipeline" element={<KanbanPage />} />
          <Route path="/deals/:id" element={<DealRedirect />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/:id" element={<CompanyDetailPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/reports" element={<LeadershipPage />} />
          <Route path="/agent" element={<AgentPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route element={<AdminLayout />}>
            <Route path="/settings/users" element={<UsersPage />} />
            <Route path="/settings/access" element={<AccessPage />} />
            <Route path="/settings/audit" element={<AuditPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/pipeline" replace />} />
      </Routes>
    </Suspense>
  );
}
